import React, { useCallback, useRef, useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import styled, { css } from 'styled-components'

import { Button, type ButtonStyles } from '@nexiahome/ui-common/components/primitives/buttons'

import KeyboardAccessible from 'components/KeyboardAccessible'

import { CardId } from 'pages/Dashboard/cardRegistry'

import { color } from 'styles/colors'
import { menuItem } from 'styles/menuItem'

const ADD_MENU_ID = 'add-card-menu'

export type RemovedCardOption = {
  id: CardId
  title: string
}

type Props = {
  isCustomizing: boolean
  isSaving: boolean
  removedCards: RemovedCardOption[]
  onCustomizeCallback: () => void
  onAddCardCallback: (id: CardId) => void
  onSaveCallback: () => void
  onCancelCallback: () => void
}

const CustomizeToolbar = ({
  isCustomizing,
  isSaving,
  removedCards,
  onCustomizeCallback,
  onAddCardCallback,
  onSaveCallback,
  onCancelCallback
}: Props) => {
  const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(null)
  const addButtonRef = useRef<HTMLDivElement>(null)

  const openAddMenu = useCallback(() => {
    setAddMenuAnchor(addButtonRef.current)
  }, [])

  const closeAddMenu = useCallback(() => {
    setAddMenuAnchor(null)
  }, [])

  const selectRemovedCard = useCallback((id: CardId) => {
    onAddCardCallback(id)
    setAddMenuAnchor(null)
  }, [onAddCardCallback])

  if (!isCustomizing) {
    return (
      <S.CustomizeWrapper>
        <Button decoration="secondary" onPress={onCustomizeCallback} styles={titleCaseButtonStyle}>
          {({ textStyles }) => (
            <S.CustomizeContent style={textStyles as React.CSSProperties}>
              <S.CustomizeIcon src="/img/customize.svg" alt="" />
              Customize
            </S.CustomizeContent>
          )}
        </Button>
      </S.CustomizeWrapper>
    )
  }

  return (
    <S.EditingButtons>
      <S.AddWrapper ref={addButtonRef}>
        <Button decoration="secondary" enabled={!isSaving} onPress={openAddMenu}>
          {({ textStyles }) => (
            <S.AddContent style={textStyles as React.CSSProperties}>
              <AddIcon fontSize="small" />
              Add
            </S.AddContent>
          )}
        </Button>
      </S.AddWrapper>
      <Menu
        id={ADD_MENU_ID}
        anchorEl={addMenuAnchor}
        open={!!addMenuAnchor}
        onClose={closeAddMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {removedCards.length === 0 ? (
          <MenuItem disabled>All cards added</MenuItem>
        ) : (
          removedCards.map(({ id, title }) => (
            <S.ExecutableMenuItem key={id} onClick={() => selectRemovedCard(id)}>
              {title}
            </S.ExecutableMenuItem>
          ))
        )}
      </Menu>
      <S.Divider aria-hidden />
      <S.CancelWrapper>
        <Button decoration="secondary" enabled={!isSaving} onPress={onCancelCallback} text="Cancel" />
      </S.CancelWrapper>
      <Button decoration="positive" enabled={!isSaving} onPress={onSaveCallback} text="Save" showSpinner={isSaving} />
    </S.EditingButtons>
  )
}

export default CustomizeToolbar

const titleCaseButtonStyle: ButtonStyles = {
  text: { textTransform: 'none' }
}

// Using "!important" here because it's the only way to override ui-common's transparent background for secondary buttons
// on a non-white surface (the dashboard toolbar sits on a grey background).
const whiteButtonStyles = css`
  button {
    background-color: ${color('secondaryButton')} !important;
  }

  button:active {
    background-color: ${color('secondaryButtonActive')} !important;
  }
`

const S = {
  CustomizeWrapper: styled.div`
    ${whiteButtonStyles}
  `,
  CustomizeIcon: styled.img`
    width: 22px;
    height: 22px;
  `,
  CustomizeContent: styled.span`
    display: flex;
    align-items: center;
    gap: 6px;
  `,
  AddContent: styled.span`
    display: flex;
    align-items: center;
    gap: 4px;
  `,
  EditingButtons: styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
  `,
  AddWrapper: styled.div`
    ${whiteButtonStyles}
  `,
  ExecutableMenuItem: styled(KeyboardAccessible)`${menuItem({ executable: true })}`,
  Divider: styled.span`
    width: 1px;
    align-self: stretch;
    background: ${color('tooltipBorder')};
    margin: 0 2px;
  `,
  CancelWrapper: styled.div`
    ${whiteButtonStyles}
  `
}
